import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { hasAccessToDashboard } from '../../utils/dashboardAccess';
import { getUserRole, hasRole, isAdmin } from '../../utils/roleUtils';
import ManagerDashboard from './ManagerDashboard';
import OperatorDashboard from './OperatorDashboard';
import PlannerDashboard from './PlannerDashboard';
import WarehouseDashboard from './WarehouseDashboard';
import WarehouseHomePage from './WarehouseHomePage';
import SupervisorHomePage from './SupervisorHomePage';
import CostingHomePage from './CostingHomePage';
import ReturnShortagesCard from './ReturnShortagesCard';
import './HomePage.css';
import { clickable } from '../../utils/a11y';

const HomePage = () => {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);

  // Parse URL parameters for dashboard selection
  const searchParams = new URLSearchParams(location.search);
  const dashboardParam = searchParams.get('dashboard');
  const selectedDashboard = dashboardParam?.toUpperCase();

  // Home screen: the primary role. Picking another dashboard: any of the user's roles.
  const userRole = getUserRole(user);

  // Determine user role type
  const isManager = () => userRole === 'MANAGER';


  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('greetings.goodMorning');
    if (hour < 18) return t('greetings.goodAfternoon');
    return t('greetings.goodEvening');
  };

  // Render dashboard component by role code
  const renderDashboardByRole = (roleCode) => {
    const role = roleCode?.toUpperCase();

    switch (role) {
      case 'MANAGER':
        return <ManagerDashboard />;
      case 'SUPERVISOR':
        return <SupervisorHomePage />;
      case 'OPERATOR':
        return <OperatorDashboard />;
      case 'PLANNER':
        return <PlannerDashboard />;
      case 'WAREHOUSE':
        return <WarehouseHomePage />;
      case 'COSTING':
        return <CostingHomePage />;
      default:
        return null;
    }
  };

  // Render welcome header for selected dashboard
  const renderWelcomeHeader = (dashboardRole) => (
    <div className="welcome-header">
      <h1 className="welcome-title-blue">
        {getGreeting()}, {user?.first_name || user?.username || t('userInfo.defaultUser')}
      </h1>
      <div className="user-info">
        <span className="user-role">
          {dashboardRole ? t('homePage.viewingDashboard', { role: dashboardRole }) : (userRole || t('userInfo.defaultRole'))}
        </span>
        <span className="separator">•</span>
        <span className="user-department">
          {t('userInfo.department')}: {user?.department_name || user?.department_code || t('userInfo.notAssigned')}
        </span>
      </div>
    </div>
  );

  // Check if user is trying to access a dashboard via URL parameter
  if (selectedDashboard && hasAccessToDashboard(user?.roles || userRole, selectedDashboard)) {
    // User has access to the requested dashboard, render it
    const dashboardComponent = renderDashboardByRole(selectedDashboard);
    if (dashboardComponent) {
      return (
        <div className="home-page flex-container">
          <div className="home-container flex-content">
            {renderWelcomeHeader(selectedDashboard)}
            {dashboardComponent}
          </div>
        </div>
      );
    }
  }

  // For supervisor users, return the component directly without HomePage wrapper
  if (userRole === 'SUPERVISOR') {
    return <SupervisorHomePage />;
  }

  // For warehouse users, return the component directly without HomePage wrapper
  if (userRole === 'WAREHOUSE') {
    return <WarehouseHomePage />;
  }

  return (
    <div className="home-page flex-container">
      <div className="home-container flex-content">
        {/* Role-based Dashboard */}
        {loading ? (
          <div className="loading">{t('homePage.loadingDashboard')}</div>
        ) : isManager() ? (
          <>
            {renderWelcomeHeader()}
            <ManagerDashboard />
          </>
        ) : userRole === 'OPERATOR' ? (
          <>
            {renderWelcomeHeader()}
            <OperatorDashboard />
          </>
        ) : userRole === 'PLANNER' ? (
          <>
            {renderWelcomeHeader()}
            <PlannerDashboard />
          </>
        ) : userRole === 'WAREHOUSE' ? (
          <>
            {renderWelcomeHeader()}
            <WarehouseDashboard />
          </>
        ) : userRole === 'COSTING' ? (
          <>
            {renderWelcomeHeader()}
            <CostingHomePage />
          </>
        ) : (
          <div className="splash-screen">
            <div className="splash-content">
              <div className="splash-logo">
                <h1 className="splash-title">Multipack ERP</h1>
              </div>

              <div className="splash-welcome">
                <h2 className="welcome-text-blue">{t('splash.welcome', { name: user?.full_name || user?.username || t('userInfo.defaultUser') })}</h2>
                <p className="splash-role-blue">{t('splash.loginAs')} <span className="role-highlight">{userRole || t('userInfo.unknown')}</span></p>
              </div>

              <div className="splash-info">
                <p>{t('splash.noDashboard')}</p>
                <p>{t('splash.useNavigation')}</p>
              </div>

              {isAdmin(user) && <ReturnShortagesCard />}

              <div className="splash-quick-links">
                <h3>{t('splash.quickLinks')}</h3>
                <div className="quick-links-grid">
                  <div className="quick-link-card" {...clickable(() => navigate('/production'))}>
                    <span className="quick-link-icon">🏭</span>
                    <span className="quick-link-text">{t('modules.production')}</span>
                  </div>
                  <div className="quick-link-card" {...clickable(() => navigate('/products'))}>
                    <span className="quick-link-icon">📦</span>
                    <span className="quick-link-text">{t('modules.products')}</span>
                  </div>
                  <div className="quick-link-card" {...clickable(() => navigate('/sales'))}>
                    <span className="quick-link-icon">💰</span>
                    <span className="quick-link-text">{t('modules.sales')}</span>
                  </div>
                  <div className="quick-link-card" {...clickable(() => navigate('/procurement'))}>
                    <span className="quick-link-icon">🛒</span>
                    <span className="quick-link-text">{t('modules.procurement')}</span>
                  </div>
                  <div className="quick-link-card" {...clickable(() => navigate('/admin'))}>
                    <span className="quick-link-icon">⚙️</span>
                    <span className="quick-link-text">{t('modules.admin')}</span>
                  </div>
                  <div className="quick-link-card" {...clickable(() => navigate('/ai'))}>
                    <span className="quick-link-icon">🤖</span>
                    <span className="quick-link-text">{t('modules.ai')}</span>
                  </div>
                </div>
              </div>

              <div className="splash-footer">
                <p className="splash-version">{t('splash.version')}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HomePage;
